import React, { useEffect, useMemo, useState } from 'react';

import BottomSheet from 'components/DesignSystem/BottomSheet';
import Snackbar from 'components/DesignSystem/Snackbar';
import DatePicker from 'components/DatePicker';
import Section from 'components/Meteorology/Shared/ControlPanelSheet/Section';
import OptionRow from 'components/Meteorology/Shared/ControlPanelSheet/OptionRow';
import SheetActionButton from 'components/Layout/MobileShell/SheetActionButton';
import useCultures from 'hooks/Meteorology/Agro/CultureIndex/Area/useCultures';
import useForecastDate from 'hooks/Meteorology/Agro/CultureIndex/Area/useForecastDate';
import useForecastPeriod from 'hooks/Meteorology/Agro/CultureIndex/Area/useForecastPeriod';
import useMapDownload from 'hooks/Meteorology/Agro/CultureIndex/Area/useMapDownload';
import useMapData from 'hooks/Meteorology/Agro/CultureIndex/Area/useMapData';
import { store } from 'redux/store';
import { reset } from 'redux/reducers/meteorologyAgroCultureIndexAreaReducer';
import { cultureIndexAreaApi } from 'services/api/Meteorology/Agro/CultureIndex/Area';
import { CultureIndexAreaMap } from 'types/Meteorology/Agro/CultureIndex/Area/map';

import './index.scss';

type SheetSection = 'culture' | 'forecast' | 'period' | '';

const isMapComplete = (map: CultureIndexAreaMap) => (
  [map.params.culture, map.params.forecastDate, map.params.periodStart, map.params.periodEnd]
    .every((value) => value !== undefined)
);

export default function ControlPanelSheet({
  open,
  dismiss,
  maps,
}: {
  open: boolean;
  dismiss: () => void;
  maps: CultureIndexAreaMap[];
}) {
  const [openSection, setOpenSection] = useState<SheetSection>('culture');
  const [activeMapId, setActiveMapId] = useState<number>(maps[0]?.id ?? 0);

  const activeMap = maps.find((m) => m.id === activeMapId) ?? maps[0];
  const activePosition = maps.findIndex((m) => m.id === activeMap.id) + 1;

  const { cultures, setCulture } = useCultures(activeMap);
  const { availableDates, forecastDate, setForecastDate } = useForecastDate(activeMap);
  const {
    availablePeriod, changePeriod, periodStart, periodEnd,
  } = useForecastPeriod(activeMap);
  const { setMapFilename } = useMapDownload();
  const { triggerMapDataRequest } = useMapData();

  useEffect(() => {
    if (open) {
      setOpenSection('culture');
      setActiveMapId(maps[0]?.id ?? 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleSection = (section: SheetSection) => () => (
    setOpenSection((current) => (current === section ? '' : section))
  );

  const firstIncomplete = useMemo(
    () => maps.findIndex((m) => !isMapComplete(m)),
    [maps],
  );
  const requestDisabled = firstIncomplete !== -1;
  const multi = maps.length > 1;

  const realize = async () => {
    await Promise.all(maps.map(async (map) => {
      setMapFilename(map);
      await triggerMapDataRequest(map);
    }));
    dismiss();
  };

  const clear = () => {
    store.dispatch(reset());
    store.dispatch(cultureIndexAreaApi.util.resetApiState());
    dismiss();
  };

  const cultureLabel = activeMap.params.culture?.label;
  const forecastLabel = activeMap.params.forecastDate
    ? new Date(activeMap.params.forecastDate).toLocaleDateString('pt-BR')
    : undefined;
  const periodLabel = periodStart && periodEnd
    ? `${periodStart.toLocaleDateString('pt-BR')} - ${periodEnd.toLocaleDateString('pt-BR')}`
    : undefined;

  return (
    <BottomSheet
      open={open}
      dismiss={dismiss}
      title="Painel de controle"
      className="agro-ci-area-control-panel-sheet"
    >
      {multi && (
        <>
          <div className="agro-ci-area-sheet-tabs" role="tablist">
            {maps.map((m, index) => (
              <button
                key={m.id}
                type="button"
                role="tab"
                aria-selected={m.id === activeMap.id}
                className={`agro-ci-area-sheet-tab${m.id === activeMap.id ? ' active' : ''}`}
                onClick={() => {
                  setActiveMapId(m.id);
                  setOpenSection('culture');
                }}
              >
                <span
                  className={`agro-ci-area-sheet-tab-dot${isMapComplete(m) ? ' complete' : ''}`}
                />
                {`Mapa ${index + 1}`}
              </button>
            ))}
          </div>
          <p className="agro-ci-area-sheet-notice">
            {`Editando os filtros do Mapa ${activePosition}. Os dois mapas são independentes.`}
          </p>
        </>
      )}

      <div className="agro-ci-area-sheet-body">
        <Section
          label="Cultura"
          valueText={cultureLabel}
          open={openSection === 'culture'}
          onToggle={toggleSection('culture')}
        >
          {cultures.length === 0 && (
            <Snackbar className="mt-2">Nenhuma cultura disponível.</Snackbar>
          )}
          {cultures.map((c) => (
            <OptionRow
              key={c.name}
              label={c.label}
              selected={c.name === activeMap.params.culture?.name}
              onSelect={() => setCulture(c)}
            />
          ))}
        </Section>

        <Section
          label="Data de previsão"
          valueText={forecastLabel}
          open={openSection === 'forecast'}
          onToggle={toggleSection('forecast')}
        >
          <DatePicker
            inline
            selected={activeMap.params.forecastDate ? forecastDate : undefined}
            onChange={setForecastDate}
            includeDates={availableDates}
          />
        </Section>

        <Section
          label="Período de previsão"
          valueText={periodLabel}
          open={openSection === 'period'}
          onToggle={toggleSection('period')}
        >
          <DatePicker
            inline
            selectsRange
            startDate={periodStart}
            endDate={periodEnd}
            onChange={changePeriod}
            includeDates={availablePeriod}
          />
        </Section>
      </div>

      <div className="agro-ci-area-sheet-footer">
        {requestDisabled && (
          <p className="agro-ci-area-sheet-hint">
            {`Complete os filtros do Mapa ${firstIncomplete + 1} para consultar.`}
          </p>
        )}
        <SheetActionButton
          variant="confirm"
          disabled={requestDisabled}
          onClick={realize}
        >
          {multi ? 'Consultar os 2 mapas' : 'Realizar consulta'}
        </SheetActionButton>
        <SheetActionButton variant="clear" onClick={clear}>
          Limpar filtros
        </SheetActionButton>
      </div>
    </BottomSheet>
  );
}
